import {
  type Account,
  type AccountBalance,
  applyBalanceFilter,
  type BalanceFilter,
  ChainType,
  defaultAccountBalance,
  fillEcosystemAddressesFromAccounts,
  type Token,
  webSupportedChainTypeKeys,
  zipAccountsWithBalances,
} from '@polaris/core/models'
import type { EcosystemAddresses } from '@polaris/core/services'
import { type GetRouteRequest, RouteErrors } from '@polaris/core/services'
import { getEcosystemAddresses } from '@polaris/core/signing'
import { featureFlagsAtom } from '@polaris/core/stores'
import { runnerSessionDataAtom } from '@polaris/core/stores/mpc/atom'
import {
  formatCoin,
  formatPrice,
  getAddress,
  getDeepError,
  getInsufficientFeeInfo,
  getInsufficientFeeWarningInfo,
  getWalletColor,
  type InsufficientFeeInfo,
} from '@polaris/core/utils'
import { fieldAtom, formAtom } from 'form-atoms'
import { atom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { atomWithQuery } from 'jotai-tanstack-query'
import type {
  ConnectableWalletOption,
  ConnectedWalletOption,
} from '@/src/hooks/wallet/use-wallet-options'
import { isAddressValidQuery } from '@/src/queries/keys/addresses'
import { chainFeeTokenDenomQuery } from '@/src/queries/keys/chains'
import { getRouteQuery } from '@/src/queries/keys/routes'
import { tokenAcrossChainsQuery } from '@/src/queries/keys/tokens'
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'
import { PRIVACY_UI } from '@/src/stores/privacy/constants'
import { isRunningAtom } from '@/src/stores/route-machine/atoms'
import { accountsAtom } from '../accounts/atoms'
import { pendingAccountsAtom, walletAccountsAtom } from '../accounts/wallets'
import { balancesByChainsAtom } from '../balances/atom'
import {
  cosmosDerivationPathsAtom,
  singletonHotkeyAtom,
} from '../hotkeys/atoms'
import { mpcNamadaKeysAtom } from '../mpc/atoms'
import { userAtom } from '../user/atoms'
import { userSettingsAtom } from '../user-settings/atoms'
import { walletKeyringAtom, walletOptionsAtom } from '../wallets/atoms'

export interface InputToken {
  chain?: string | null
  token?: string | null
}

export interface BalanceInputToken extends InputToken {
  /**
   * If multiple balances are available for the same chain/token across accounts,
   * this can be used to select a specific account's balance.
   */
  address?: string | null
}

// TODO: Add sync with query params and with latest value cached inside cookies

const fromFieldAtom = fieldAtom<BalanceInputToken | undefined>({
  value: undefined,
})

/**
 * Used to provide a list of balances for the selected from token
 * across all accounts in the case of selecting an aggregated balance (where multiple addresses are available for the same chain/token).
 * So the user can select a specific account's balance via the BalanceInputToken.address from field state to transact with.
 */
const fromBalancesAtom = atom((get) => {
  const from = get(get(fromFieldAtom).value)
  const balancesByChains = get(balancesByChainsAtom)
  const userSettings = get(userSettingsAtom)

  const chainBalances =
    balancesByChains
      .find(
        (balancesByChain) =>
          balancesByChain.chain_id === from?.chain ||
          balancesByChain.chain_name === from?.chain,
      )
      ?.balances.filter((balance) => balance.denom === from?.token) ?? []

  // Apply balance filter based on user settings
  const filter: BalanceFilter = {
    minValueUsd: userSettings.hideSmallBalances
      ? userSettings.smallestBalanceUsd
      : undefined,
  }

  return chainBalances.filter((balance) => applyBalanceFilter(filter, balance))
})

/**
 * Returns the balance for the selected from token across all accounts.
 * If no balance or address is specified in the case of aggregated balances,
 * the default account balance is returned.
 */
export const fromBalanceAtom = atom((get) => {
  const from = get(get(fromFieldAtom).value)
  const walletAccounts = get(walletAccountsAtom)
  const fromBalances = get(fromBalancesAtom)

  if (!fromBalances.length) return undefined

  if (from?.address) {
    return fromBalances.find((balance) => from.address === balance.address)
  }

  if (fromBalances.length === 1) {
    return fromBalances[0]
  }

  return defaultAccountBalance(fromBalances, walletAccounts.accountsWithWallets)
    ?.balance
})

/** Controls swap tool popover for selecting a multi-account balance */
export const isSelectingMultiAccountFromBalanceAtom = atom(false)

/**
 * If from balance resides in >1 account, provides a mapping of
 * each balance with the corresponding profile account and connected wallet option (if present).
 */
export const fromAccountsBalancesAtom = atom((get) => {
  const profileAccounts = get(walletAccountsAtom)
  const { accountsWithWallets: pendingAccountsWithWallets } =
    get(pendingAccountsAtom)
  const multiAccountBalances = get(fromBalancesAtom)

  if (!multiAccountBalances || multiAccountBalances.length === 0) return []

  return zipAccountsWithBalances(
    multiAccountBalances,
    profileAccounts.accountsWithWallets.concat(pendingAccountsWithWallets),
  )
})

/**
 * Returns a function that takes a direction ('from' or 'to') and returns wallet account info.
 * Returns either wallet/account info object, 'manual-address-input' string, or undefined.
 */
export const getWalletAccountInfoAtom = atom((get) => {
  const { accountsWithWallets: walletAccounts } = get(walletAccountsAtom)
  const fromAccountsBalances = get(fromAccountsBalancesAtom)
  const fromBalance = get(fromBalanceAtom)
  const toWalletAddress = get(toWalletAddressAtom)
  const manualToWalletAddress = get(manualToWalletAddressAtom)
  const { connectedWallets } = get(walletOptionsAtom)
  const fromToken = get(fromTokenAcrossChainsAtom)
  const toToken = get(toTokenAcrossChainsAtom)
  const featureFlags = get(featureFlagsAtom)

  return (direction: 'from' | 'to') => {
    const token = direction === 'from' ? fromToken : toToken
    const tokenChainType = token.data?.requested_asset.chain_type

    // Show Polaris logo for Privacy Vault (Namada) for both directions - only for privacyUi flow
    if (tokenChainType === ChainType.Namada && featureFlags.privacyUi) {
      return {
        img: PRIVACY_UI.POLARIS_LOGO,
        name: 'Polaris',
        color: getWalletColor('Polaris', 'MPCVault'),
        isMultiAccount: false,
      }
    }

    if (direction === 'from') {
      // Sort BrowserExtension accounts first to prefer wallet logos for accounts
      // vs i.e. watch address icons or mobile wallet icons
      const sortedAccounts = [...fromAccountsBalances].sort((a, b) => {
        if (a.type === 'BrowserExtension' && b.type !== 'BrowserExtension')
          return -1
        if (a.type !== 'BrowserExtension' && b.type === 'BrowserExtension')
          return 1
        return 0
      })
      const fromAccountBalance = sortedAccounts.find(
        (account) => account.address === fromBalance?.address,
      )

      if (fromAccountBalance) {
        return {
          img: fromAccountBalance.provider_info.img,
          name: fromAccountBalance.provider_info.name,
          color: getWalletColor(
            fromAccountBalance.provider_info.name,
            fromAccountBalance.type,
          ),
          isMultiAccount: true,
        }
      }
    } else {
      // For 'to' direction, handle destination address selection

      // Assume it is manual input if an account is not found
      if (manualToWalletAddress && toWalletAddress === manualToWalletAddress) {
        return 'manual-address-input'
      }
      const account = walletAccounts.find(
        (account) => account.address === toWalletAddress,
      )

      if (account) {
        return {
          img: account.provider_info.img,
          name: account.provider_info.name,
          address: account.address,
          account,
          color: getWalletColor(account.provider_info.name, account.type),
        }
      }
    }

    // Fallback to connected wallet for the chain type
    if (tokenChainType) {
      const connectedWallet = connectedWallets[tokenChainType]
      if (connectedWallet) {
        return {
          img: connectedWallet.logoUrl,
          name: connectedWallet.displayName,
          color: getWalletColor(connectedWallet.displayName),
        }
      }
    }
  }
})

const toFieldAtom = fieldAtom<InputToken | undefined>({
  value: undefined,
})

const chainFeeTokenDenomAtom = atomWithQuery((get) => {
  const token = get(fromTokenAcrossChainsAtom).data?.requested_asset
  return chainFeeTokenDenomQuery({
    chainId: token?.chain_id,
    chainType: token?.chain_type,
  })
})

const chainFeeTokenBalanceAtom = atom((get) => {
  const from = get(get(fromFieldAtom).value)
  const feeToken = get(chainFeeTokenDenomAtom).data
  const balancesByChains = get(balancesByChainsAtom)

  return balancesByChains
    .find(
      (balancesByChain) =>
        balancesByChain.chain_id === from?.chain ||
        balancesByChain.chain_name === from?.chain,
    )
    ?.balances.find((balance) => balance.denom === feeToken)
})

const fromAmountFieldAtom = fieldAtom({
  value: '',
  validate(state) {
    const { value, get } = state
    const balance = get(fromBalanceAtom)

    const amount = +value
    const available = balance?.normalized_amount ?? 0

    if (amount > available || amount <= 0) {
      return ['Balance not available']
    }

    return []
  },
})

export const DEFAULT_SLIPPAGE = '5'

/**
 * This atom manage all the logic related to the swap form,
 * we should manage the validation here not outside.
 */
export const swapFormAtom = formAtom({
  fromAmount: fromAmountFieldAtom,
  from: fromFieldAtom,
  to: toFieldAtom,
  slippage: fieldAtom({
    value: DEFAULT_SLIPPAGE,
  }),
})

/**
 * Run side effect for the swap form, if from asset change we need
 * to validate again the form using the new balance.
 */
export const fromBalanceEffectAtom = atomEffect((get, set) => {
  get(fromBalanceAtom)
  set(get(get(get(swapFormAtom).fields).fromAmount).validate)
})

/**
 * Retrieve from token metadata, for displaying it inside the swap form.
 */
export const fromTokenAcrossChainsAtom = atomWithQuery((get) => {
  const chain = get(get(swapFormAtom).values).from?.chain
  const token = get(get(swapFormAtom).values).from?.token

  return tokenAcrossChainsQuery(chain, token)
})

/**
 * Retrieve to token metadata, for displaying it inside the swap form.
 */
export const toTokenAcrossChainsAtom = atomWithQuery((get) => {
  const chain = get(get(swapFormAtom).values).to?.chain
  const token = get(get(swapFormAtom).values).to?.token

  return tokenAcrossChainsQuery(chain, token)
})

export const fromTokenUSDAmountAtom = atom((get) => {
  const fromToken = get(fromTokenAcrossChainsAtom).data?.requested_asset
  const fromAmount = get(get(fromAmountFieldAtom).value)

  return fromToken?.price_usd &&
    fromAmount &&
    !Number.isNaN(Number.parseFloat(fromAmount))
    ? formatPrice(
        Number.parseFloat(fromAmount) * Number.parseFloat(fromToken.price_usd),
      )
    : '$0'
})

/**
 * Private atom that stores a map of chain ID to manual wallet address input.
 *
 * We key by chain ID since cosmos chains require a unique address format per chain.
 * And, when users switch chains the manual address input is cleared.
 */
const _manualToWalletAddressMapAtom = atom<Map<string, string>>(new Map())

/**
 * Read-write atom that gets/sets the manual wallet address for the current to asset chain ID.
 */
export const manualToWalletAddressAtom = atom(
  (get) => {
    const toChainAsset = get(toTokenAcrossChainsAtom).data?.requested_asset
    const toChainId = toChainAsset?.chain_id
    if (!toChainId) return undefined

    const addressMap = get(_manualToWalletAddressMapAtom)
    return addressMap.get(toChainId)
  },
  (get, set, newAddress: string | undefined) => {
    const toChainAsset = get(toTokenAcrossChainsAtom).data?.requested_asset
    const toChainId = toChainAsset?.chain_id
    if (!toChainId) return

    const prev = get(_manualToWalletAddressMapAtom)
    const next = new Map(prev)

    if (newAddress === undefined) {
      next.delete(toChainId)
    } else {
      next.set(toChainId, newAddress)
    }

    set(_manualToWalletAddressMapAtom, next)
  },
)

const isValidManualToWalletAddressAtom = atomWithQuery((get) => {
  const manualToWalletAddress = get(manualToWalletAddressAtom)
  const toChainAsset = get(toTokenAcrossChainsAtom).data?.requested_asset
  const toChainType = toChainAsset?.chain_type
  const toChainId = toChainAsset?.chain_id

  return isAddressValidQuery({
    address: manualToWalletAddress ?? '',
    chainType: toChainType,
    chainId: toChainId,
  })
})

/**
 * Map of destination chain type to selected address.
 * Allows setting and retrieving the address for a given chain type.
 * This stores the users preferred account ecosystem (aka non derived) address for a given chain.
 * This should map to accounts that are part of their polaris accounts.
 */
const _selectedToChainAddressesAtom = atom<Map<ChainType, string>>(new Map())

export const selectedToChainAddressesAtom = atom(
  (get) => get(_selectedToChainAddressesAtom),
  (get, set, [chainType, address]: [ChainType, string]) => {
    const prev = get(_selectedToChainAddressesAtom)
    const accounts = get(accountsAtom)
    const account = accounts.find((account) => account.address === address)
    if (!account) {
      // Avoid setting an address that is not in the account set
      return
    }
    // Clean up any preferences that are not in the account set
    for (const [_chainId, currentAddress] of prev) {
      if (!accounts.some((account) => account.address === currentAddress)) {
        prev.delete(_chainId)
      }
    }
    const next = new Map(prev)
    next.set(chainType, address)
    set(_selectedToChainAddressesAtom, next)
    // Clear manual address input
    set(manualToWalletAddressAtom, undefined)
  },
)

/**
 * Selected or determined destination wallet address.
 */
export const toWalletAddressAtom = atom((get) => {
  const manualToWalletAddress = get(manualToWalletAddressAtom)
  const isValidManualToWalletAddress = get(
    isValidManualToWalletAddressAtom,
  )?.data
  const selectedAddresses = get(selectedToChainAddressesAtom)
  const walletKeyring = get(walletKeyringAtom)
  const accounts = get(accountsAtom)
  const fromBalance = get(fromBalanceAtom)
  const fromChainAsset = get(fromTokenAcrossChainsAtom).data?.requested_asset
  const fromChainType = fromChainAsset?.chain_type
  const fromChainId = fromChainAsset?.chain_id
  const toChainAsset = get(toTokenAcrossChainsAtom).data?.requested_asset
  const toChainType = toChainAsset?.chain_type
  const toChainId = toChainAsset?.chain_id

  // Valid manual address
  if (manualToWalletAddress && isValidManualToWalletAddress) {
    return manualToWalletAddress
  }

  /** Use selected an address they prefer */
  const selectedAddress = toChainType
    ? selectedAddresses.get(toChainType)
    : undefined

  if (selectedAddress) {
    return selectedAddress
  }

  // Same chain without selected address. Select the same keyring.
  if (
    fromChainId &&
    toChainId &&
    fromChainType &&
    toChainType &&
    fromChainId === toChainId
  ) {
    return walletKeyring.getAddress(toChainType)
  }

  // If user hasn't selected an address and is on diff chains
  // Try to return an address that's on the same private key account for convenience.
  const fromAccount = accounts.find(
    (account) => account.address === fromBalance?.address,
  )
  const sameToAccount = fromAccount
    ? accounts.find(
        (account) =>
          account.grouping_key === fromAccount.grouping_key &&
          account.ecosystem === toChainType,
      )
    : undefined
  if (sameToAccount) {
    return sameToAccount.address
  }

  // If they don't have same account, just select first account that will work for that ecosystem
  const toAccount = accounts.find(
    (account) => account.ecosystem === toChainType,
  )
  if (toAccount) {
    return toAccount.address
  }

  if (toChainType) {
    return walletKeyring.getAddress(toChainType)
  }

  return undefined
})

/** Converted from base ecosystem address to destination chain address. (i.e. cosmos address to destination Osmosis chain address) */
const toWalletChainAddressAtom = atom((get) => {
  const toChainAsset = get(toTokenAcrossChainsAtom).data?.requested_asset
  const toWalletAddress = get(toWalletAddressAtom)
  const derivationPaths = get(cosmosDerivationPathsAtom)

  if (
    !toWalletAddress ||
    !toChainAsset?.chain_type ||
    !toChainAsset?.chain_id
  ) {
    return undefined
  }

  try {
    return getAddress(
      toWalletAddress,
      toChainAsset.chain_type,
      toChainAsset.chain_id,
      derivationPaths,
    )
  } catch (error) {
    console.error('Failed to derive chain address:', error)
    return toWalletAddress
  }
})

/**
 * Computes the final swap address override if a destination address is selected
 * that differs from the default keyring address.
 */
export const finalSwapAddressOverwriteAtom = atom((get) => {
  const toWalletAddress = get(toWalletAddressAtom)
  const toWalletChainAddress = get(toWalletChainAddressAtom)
  const toTokenAcrossChains = get(toTokenAcrossChainsAtom).data?.requested_asset
  const walletKeyring = get(walletKeyringAtom)

  // Only overwrite address if needed
  if (
    toWalletAddress &&
    toTokenAcrossChains &&
    // No point in setting it if it's the same as the keyring address
    toWalletAddress !==
      walletKeyring.getAddress(toTokenAcrossChains.chain_type) &&
    toWalletChainAddress
  ) {
    return toWalletChainAddress
  }

  return undefined
})

export const SWAP_ROUTE_REFETCH_INTERVAL_MS = 10_000

export const swapRouteQueryEnabledAtom = atom((get) => {
  const to = get(get(swapFormAtom).values).to
  const from = get(get(swapFormAtom).values).from
  const submitStatus = get(get(swapFormAtom).submitStatus)
  const walletKeyring = get(walletKeyringAtom)
  const { trading } = get(featureFlagsAtom)

  const mpcWalletAddresses = get(mpcWalletAddressesWithNamadaOverrideAtom)

  const ecosystemToAddrMain: EcosystemAddresses | undefined =
    mpcWalletAddresses.data ?? getEcosystemAddresses(walletKeyring)

  const ecosystemToAddrFinalDest = getEcosystemAddresses(walletKeyring)

  const ecosystemAddresses =
    ecosystemToAddrMain &&
    Object.values(ecosystemToAddrMain).some((value) => !!value) &&
    Object.values(ecosystemToAddrFinalDest).some((value) => !!value)
      ? {
          ecosystemToAddrMain,
          ecosystemToAddrFinalDest,
        }
      : undefined

  const searchParams = {
    fromChain: from?.chain,
    fromAsset: from?.token,
    toChain: to?.chain,
    toAsset: to?.token,
    enableFeeRequirements: ecosystemAddresses ? 'true' : 'false',
  }

  return (
    ecosystemToAddrMain &&
    trading &&
    searchParams &&
    Object.values(searchParams).every((param) => !!param) &&
    submitStatus === 'idle'
  )
})

// Memoization layer: Only recompute route params when actual chain/denom/amount values change
// This prevents cascading atom updates when form objects get new references
const swapRouteParamsAtom = atom((get) => {
  const to = get(get(swapFormAtom).values).to
  const from = get(get(swapFormAtom).values).from
  const fromToken = get(fromTokenAcrossChainsAtom)?.data
  const fromAmount = get(get(swapFormAtom).values).fromAmount
  const walletKeyring = get(walletKeyringAtom)
  const accounts = get(accountsAtom)
  const { privacy } = get(featureFlagsAtom)

  const mpcWalletAddresses = get(mpcWalletAddressesWithNamadaOverrideAtom)
  const mpcNamadaKeys = get(mpcNamadaKeysAtom)
  const finalSwapAddressOverwrite = get(finalSwapAddressOverwriteAtom)
  // Want to use this^, however it gets converted to chain address (i.e. celestia) but the accounts store cosmos addresses as cosmos hub addresses
  // Use toWalletAddressAtom and pass to fillEcosystemAddressesFromAccounts when selecting accounts

  // Reminder: main = autopilot if applicable
  const ecosystemToAddrMain: EcosystemAddresses | undefined =
    mpcWalletAddresses.data ?? getEcosystemAddresses(walletKeyring)

  // Reminder: finalDest = user/source keyring
  const fromChainType = fromToken?.requested_asset.chain_type
  const ecosystemToAddrFinalDest = fillEcosystemAddressesFromAccounts(
    accounts,
    walletKeyring,
    webSupportedChainTypeKeys,
    fromChainType,
  )

  if (privacy) {
    ecosystemToAddrFinalDest.namada = mpcWalletAddresses.data?.namada

    // Add pseudo extended key for privacy routes
    if (mpcNamadaKeys.data?.pseudoExtendedKey) {
      ecosystemToAddrFinalDest['namada:pseudo-extended-key'] =
        mpcNamadaKeys.data.pseudoExtendedKey
    }
  }

  const addresses =
    ecosystemToAddrMain &&
    Object.values(ecosystemToAddrMain).some((value) => !!value) &&
    Object.values(ecosystemToAddrFinalDest).some((value) => !!value)
      ? {
          ecosystemToAddrMain,
          ecosystemToAddrFinalDest,
          finalSwapAddressOverwrite,
        }
      : undefined

  // Return stable params object - only changes when actual values change
  return {
    fromChain: from?.chain,
    fromAsset: from?.token,
    toChain: to?.chain,
    toAsset: to?.token,
    amount: fromAmount || '0',
    addresses,
    enableFeeRequirements: addresses ? 'true' : 'false',
  }
})

export const swapRouteAtom = atomWithQuery((get) => {
  const params = get(swapRouteParamsAtom)
  const enabled = get(swapRouteQueryEnabledAtom)

  const searchParamsWithDefaultAmount = {
    fromChain: params.fromChain,
    fromAsset: params.fromAsset,
    toChain: params.toChain,
    toAsset: params.toAsset,
    amount: params.amount,
    enableFeeRequirements: params.enableFeeRequirements,
  }

  const getRefetchInterval = ({ error }: { error: Error | null }) => {
    if (error) return false

    const amount = Number.parseFloat(params.amount)

    if (amount > 0) {
      return SWAP_ROUTE_REFETCH_INTERVAL_MS // Poll every 10 seconds for non-zero amounts
    }

    return false // Don't poll for zero or no amount
  }

  return getRouteQuery(
    searchParamsWithDefaultAmount as GetRouteRequest,
    params.addresses,
    {
      refetchInterval: (query) =>
        getRefetchInterval({ error: query.state.error }),
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      gcTime: 10_000,
      staleTime: 5_000,
      enabled,
    },
  )
})

export const amountOutUSDAtom = atom((get) => {
  const data = get(swapRouteAtom).data

  return data?.route &&
    !Number.isNaN(data.route.AmountOut) &&
    data.route.AmountOut > 0
    ? formatPrice(data.route.AmountOut * data.route.OutputAssetUSDPrice)
    : '$0'
})

export const amountOutAtom = atom((get) => {
  const data = get(swapRouteAtom).data
  const toToken = get(toTokenAcrossChainsAtom).data?.requested_asset

  if (!toToken) {
    return undefined
  }

  return formatCoin(
    {
      amount: data?.route.AmountOut ?? 0,
    },
    { maximumFractionDigits: toToken?.precision, maximumSignificantDigits: 6 },
  )
})

const totalFeesUSDAtom = atom((get) => {
  const data = get(swapRouteAtom).data
  return (
    data?.route.FeeRequirements?.cost_check_result.funds_required_display.usd ??
    0
  )
})

export const priceImpactAtom = atom((get) => {
  const data = get(swapRouteAtom).data
  if (data?.route === undefined) return
  const amountInUSD = data.route.AmountIn * data.route.InputAssetUSDPrice
  const amountOut = data.route.AmountOut * data.route.OutputAssetUSDPrice
  return (1 - amountOut / amountInUSD) * -1
})

const feesImpactAtom = atom((get) => {
  const data = get(swapRouteAtom).data
  const totalFees = get(totalFeesUSDAtom)
  if (data?.route === undefined) return
  const amountInUSD = data.route.AmountIn * data.route.InputAssetUSDPrice
  return totalFees / amountInUSD
})

function getChainNotSupportedByWallet(
  token?: Token,
  connectedWallets?: Partial<Record<ChainType, ConnectedWalletOption>>,
): { chainName: string; walletName: string } | undefined {
  if (!token?.chain_type || typeof window === 'undefined') return
  const connectedWallet = connectedWallets?.[token.chain_type as ChainType]
  if (!connectedWallet || !connectedWallet.onlySupportsChains) return

  const isChainNotSupported = !connectedWallet.onlySupportsChains.some(
    (chain) => chain === token.chain_id,
  )

  if (isChainNotSupported) {
    return {
      chainName: token.chain_name,
      walletName: connectedWallet.displayName,
    }
  }
}

/**
 * Consolidated error atom that computes all route-related errors in a single pass.
 * This reduces atom count and improves performance by computing all errors together.
 */
export const routeErrorsAtom = atom((get) => {
  const routeError = get(swapRouteAtom).error
  const priceImpact = get(priceImpactAtom)
  const feesImpact = get(feesImpactAtom)

  // Early return if no errors at all
  if (!routeError && priceImpact === undefined && feesImpact === undefined) {
    return null
  }

  const deepError = getDeepError(routeError)
  const cause = routeError?.cause ? (routeError.cause as string) : ''
  const message = routeError?.message ?? ''

  // Parse address type invalid error
  const addressTypeInvalidError = (() => {
    if (!routeError) return undefined
    const isInvalid = /address type is invalid for chain .*/.test(cause)
    const chainId = cause?.match(/chain .*/)?.[0].split(' ')[1]
    return isInvalid && chainId
      ? { message: 'Invalid address type', chainId }
      : undefined
  })()

  // Parse minimum amount errors
  const minAmountError = (() => {
    if (!routeError) return undefined

    // HyperCore minimum amount errors
    const hypercoreMatch = cause.match(
      /Swap route (depositing to|withdrawing from|spot trading on) HyperCore requires a minimum of ([\d.]+) USD/i,
    )
    if (hypercoreMatch) {
      return { message: `${formatPrice(hypercoreMatch[2])} minimum` }
    }

    return undefined
  })()

  const noRouteAvailable =
    deepError?.message.includes(RouteErrors.NoAvailableRoute) ||
    deepError?.message.includes(RouteErrors.NoAvailableResults) ||
    deepError?.message.includes(RouteErrors.AssetNotSupported) ||
    deepError?.message.includes(RouteErrors.NoRouteFound)

  return {
    noRouteAvailable,
    generic: message !== '' && !noRouteAvailable,
    priceImpactHigh: priceImpact !== undefined && Math.abs(priceImpact) >= 0.1,
    feesImpactHigh: feesImpact !== undefined && feesImpact >= 0.5,
    chainNotFound: routeError
      ? /\bchain\b.*\bnot found\b/.test(message)
      : false,
    addressTypeInvalid: addressTypeInvalidError,
    noRouteIncreaseInputAmount: cause.includes(
      RouteErrors.NoRouteFoundIncreaseAmount,
    ),
    invalidDestinationAddress:
      message.includes('FinalSwapAddressOverwrite') ||
      message.includes('chain type mismatch')
        ? 'Invalid destination address for the selected chain'
        : undefined,
    minAmount: minAmountError,
  }
})

// Because of a limitation in the form-atoms library, we need to use a separate atom to reset the submit status.
// This is because the form-atoms library does not support resetting the submit status to 'idle' when the
// form is submitted, limiting how we can raise errors.
export const queueResetSubmitStatusAtom = atom(false)
export const submitStatusAtomEffect = atomEffect((get, set) => {
  const submitStatus = get(get(swapFormAtom).submitStatus)
  if (submitStatus === 'submitted' && get(queueResetSubmitStatusAtom)) {
    set(queueResetSubmitStatusAtom, false)
    set(get(swapFormAtom).submitStatus, 'idle')
  }
})

export const insufficientBalanceErrorAtom = atom((get) => {
  const { addresses } = get(walletKeyringAtom)
  const { errors: fromAmountErrorsAtom, value: fromAmountValueAtom } =
    get(fromAmountFieldAtom)
  const errors = get(fromAmountErrorsAtom)
  const formFilled = Number(get(fromAmountValueAtom)) > 0
  const user = get(userAtom)

  const routeErrors = get(routeErrorsAtom)

  // Allow user to tire kick routes without being logged in
  if (user === null) {
    return false
  }

  return (
    Object.values(addresses).length > 0 &&
    formFilled &&
    errors.length > 0 &&
    errors[0] === 'Balance not available' &&
    !routeErrors?.noRouteAvailable
  )
})

export const manualInsufficientBalanceForFeesErrorAtom = atom<
  InsufficientFeeInfo | undefined
>(undefined)

export const insufficientBalanceForFeesErrorAtom = atom((get) => {
  const data = get(swapRouteAtom).data
  const manualInsufficientBalanceForFeesError = get(
    manualInsufficientBalanceForFeesErrorAtom,
  )

  if (manualInsufficientBalanceForFeesError) {
    return manualInsufficientBalanceForFeesError
  }

  if (data?.route) {
    return getInsufficientFeeInfo(data.route)
  }
})

// This atom is used to check if there is a warning for insufficient balance for fees.
// This warning does not nblock the route but informs the user that they need to be aware that a route failure is possible.
export const insufficientBalanceForFeesWarningAtom = atom((get) => {
  const data = get(swapRouteAtom).data

  if (data?.route) {
    return getInsufficientFeeWarningInfo(data.route)
  }
})

// #region Hotkeys

/** If defined, it should be used for quotes and route initialization. */
export const swapHotkeyAtom = atom((get) => {
  return get(singletonHotkeyAtom)?.hotkey
})

/** All preconditions for using hotkey for the swap have been met, but the user needs to sign the generation of the hotkey in their wallet. */
const userNeedsToGenerateHotkeyAtom = atom((get) => {
  const runnerSessionData = get(runnerSessionDataAtom)

  // Hotkeys are not needed on the server, wallets are client only and needed for derivation
  if (typeof window === 'undefined') return false

  // Tokens must be selected
  const fromToken = get(fromTokenAcrossChainsAtom).data?.requested_asset
  if (!fromToken) return false

  // Tokens must be selected for different chains
  const toToken = get(toTokenAcrossChainsAtom).data?.requested_asset
  if (!toToken || toToken.chain_id === fromToken.chain_id) return false

  // Wallet addresses/accounts must be present for source chain/token
  const { addresses } = get(walletKeyringAtom)
  const fromAddress = addresses[fromToken.chain_type]
  if (!fromAddress) return false

  // Need derivation paths to generate hotkey
  const cosmosDerivationPaths = get(cosmosDerivationPathsAtom)
  if (!cosmosDerivationPaths) return false

  return !runnerSessionData
})

// #endregion

// Stable token pair ID - prevents swapRequirementsAtom recalculation when token objects change reference
// but underlying chain_id/denom values stay the same
const tokenPairIdAtom = atom((get) => {
  const fromToken = get(fromTokenAcrossChainsAtom).data?.requested_asset
  const toToken = get(toTokenAcrossChainsAtom).data?.requested_asset

  // Create stable string identifier from token properties
  const fromId = fromToken ? `${fromToken.chain_id}:${fromToken.denom}` : 'none'
  const toId = toToken ? `${toToken.chain_id}:${toToken.denom}` : 'none'

  return `${fromId}|${toId}`
})

export const swapRequirementsAtom = atom((get) => {
  // Subscribe to stable token pair ID instead of raw token objects
  // This prevents recalculation when token objects get new references
  get(tokenPairIdAtom)

  const validateStatus = get(get(swapFormAtom).validateStatus)
  const dirty = get(get(swapFormAtom).dirty)

  const featureFlags = get(featureFlagsAtom)
  const fromToken = get(fromTokenAcrossChainsAtom).data?.requested_asset
  const fromBalance = get(fromBalanceAtom)
  const fromAccountsBalances = get(fromAccountsBalancesAtom)
  const { accountsWithWallets: walletAccounts } = get(walletAccountsAtom)
  const { accountsWithWallets: pendingAccountsWithWallets } =
    get(pendingAccountsAtom)
  const toToken = get(toTokenAcrossChainsAtom).data?.requested_asset
  const toWalletChainAddress = get(toWalletChainAddressAtom)
  const isValidManualToWalletAddress = get(isValidManualToWalletAddressAtom)

  const { addresses } = get(walletKeyringAtom)
  const { connectedWallets, connectableWallets } = get(walletOptionsAtom)

  const userNeedsToGenerateHotkey = get(userNeedsToGenerateHotkeyAtom)

  const { data: route, isLoading: isLoadingRoute } = get(swapRouteAtom)
  const isQueryEnabled = get(swapRouteQueryEnabledAtom)
  const routeErrors = get(routeErrorsAtom)
  const isRunning = get(isRunningAtom)
  const insufficientBalanceError = get(insufficientBalanceErrorAtom)
  const insufficientBalanceForFeesError = get(
    insufficientBalanceForFeesErrorAtom,
  )
  const mpcNamadaKeys = get(mpcNamadaKeysAtom)
  const fromBalanceAccount = walletAccounts.find(
    (account) => account.address === fromBalance?.address,
  )
  const toWalletAccountInfo = get(getWalletAccountInfoAtom)('to')
  const shouldConsiderRouteLoading = isLoadingRoute && isQueryEnabled

  // Context: https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
  const isBitcoin =
    fromToken?.chain_type === ChainType.Bitcoin ||
    toToken?.chain_type === ChainType.Bitcoin

  let isFromWalletNotConnected =
    fromToken?.chain_type && addresses[fromToken.chain_type] === undefined

  // Not a requirement when selecting dest accounts
  let isToWalletNotConnected = false

  if (featureFlags.privacy) {
    if (fromToken?.chain_type === ChainType.Namada) {
      isFromWalletNotConnected = false
    }

    if (toToken?.chain_type === ChainType.Namada) {
      isToWalletNotConnected = false
    }
  }

  if (isBitcoin) {
    isFromWalletNotConnected = false
  }

  const requiredUnconnectedWalletChainName =
    isFromWalletNotConnected && !isBitcoin
      ? fromToken?.chain_name
      : isToWalletNotConnected
        ? toToken?.chain_name
        : ''

  let unsupportedChainByWallet = getChainNotSupportedByWallet(
    fromToken,
    connectedWallets,
  )

  // Handle edge case where from account balance is not for this unsupported wallet and the user needs to connect it
  if (unsupportedChainByWallet && fromBalance) {
    const fromAccountBalance = fromAccountsBalances.find(
      (account) => account.address === fromBalance.address,
    )
    const connectedWallet = connectedWallets[fromBalance.chain_type]
    // If the from account balance is associated with a different wallet than that connected with unsupported chains,
    // clear the unsupported chain condition.
    // i.e. prevents "Phantom does not support Unichain" message when you have a Leap balance selected and need to connect Leap, which is confusing
    if (
      fromAccountBalance &&
      connectedWallet &&
      connectedWallet.displayName !== fromAccountBalance.provider_info.name
    ) {
      unsupportedChainByWallet = undefined
    }
  }

  let requiredBalanceAccountToSwitch: {
    /**
     * Present if there's a from chain type wallet connected, and the account in the wallet just needs to be switched.
     * `undefined` if there's no from chain type wallet connected at all, the user just needs to find the wallet and account to switch to.
     */
    from?: ConnectedWalletOption
    /** This type contains saved wallet provider info metadata, like icons and names. */
    fromAccount?: Account
    /** This type contains saved wallet provider info metadata, like icons and names. */
    to: AccountBalance
    /**
     * If we can just connect to the to wallet, this will be populated and available for connection.
     * If it's not populated, it means the to wallet is already connected, and the user just needs to switch to the account.
     */
    toWallet?: ConnectableWalletOption
  } | null = null
  // Check if current wallet is not aligned with current from token balance selection
  if (
    fromAccountsBalances.length > 0 &&
    fromBalance &&
    fromBalanceAccount?.type !== 'WatchAddress' &&
    addresses[fromBalance.chain_type] !== fromBalance.address
  ) {
    const currentAddress = fromBalance
      ? addresses[fromBalance.chain_type]
      : undefined
    const fromAccount = walletAccounts.find(
      (account) => account.address === currentAddress,
    )
    const toAccountBalance = fromAccountsBalances.find(
      (account) => account.address === fromBalance.address,
    )
    if (toAccountBalance) {
      let toWallet: ConnectableWalletOption | undefined
      // Ensure connected wallet for that ecosystem is a different wallet than the one we're switching to
      if (
        connectedWallets[toAccountBalance.ecosystem]?.displayName !==
        toAccountBalance.provider_info.name
      ) {
        toWallet = connectableWallets[toAccountBalance.ecosystem]?.find(
          (wallet) =>
            wallet.displayName === toAccountBalance.provider_info.name,
        )
      }

      requiredBalanceAccountToSwitch = {
        from: connectedWallets[fromBalance.chain_type],
        fromAccount,
        to: toAccountBalance,
        toWallet,
      }
    }
  }

  const finalKeyringPendingAccount = fromBalance
    ? pendingAccountsWithWallets.find(
        (account) => account.address === addresses[fromBalance.chain_type],
      )
    : undefined

  const isToAccountNotSelected = // If we're doing a privacy vault swap we don't need this check
    !(toToken?.chain_type === ChainType.Namada && featureFlags.privacy) &&
    toWalletAccountInfo === undefined

  const isPrivacyVaultSwap = fromToken?.chain_type === ChainType.Namada
  const isToPrivacyVaultSwap = toToken?.chain_type === ChainType.Namada

  // Check if Namada keys are still loading for privacy vault swaps
  const isLoadingNamadaKeys =
    featureFlags.privacy && isPrivacyVaultSwap && mpcNamadaKeys.isLoading

  const enabled = !!(
    validateStatus === 'valid' &&
    dirty &&
    route &&
    !shouldConsiderRouteLoading &&
    !isRunning &&
    !unsupportedChainByWallet &&
    !routeErrors?.noRouteAvailable &&
    !insufficientBalanceError &&
    !insufficientBalanceForFeesError &&
    !userNeedsToGenerateHotkey &&
    !requiredBalanceAccountToSwitch &&
    Boolean(toWalletChainAddress) &&
    !isValidManualToWalletAddress.isFetching &&
    !isBitcoin &&
    !isToAccountNotSelected &&
    !isLoadingNamadaKeys
  )

  return {
    fromBalanceAccount,
    isFromWalletNotConnected,
    isToWalletNotConnected,
    isToAccountNotSelected,
    requiredUnconnectedWalletChainName,
    fromChainNameNotSupportedByWallet: unsupportedChainByWallet,
    unsupportedChainByWallet,
    userNeedsToGenerateHotkey,
    insufficientBalance: insufficientBalanceError,
    finalKeyringPendingAccount,
    requiredBalanceAccountToSwitch,
    enabled,
    isPrivacyVaultSwap,
    isBitcoin,
    isLoadingNamadaKeys,
    isToPrivacyVaultSwap,
  }
})

export const feeAtom = atom((get) => {
  const { data } = get(swapRouteAtom)
  return data?.route.FeeRequirements?.cost_check_result.fee
})

// State machine for "Use max" functionality
// - 'default': Initial state, showing "X available"
// - 'first-click': Type A/B show max, Type C shows max-minus-dust
// - 'second-click': Type C only, shows max-minus-fees (no dust)
export type MaxBalanceState = 'default' | 'first-click' | 'second-click'

export const maxBalanceStateAtom = atom<MaxBalanceState>('default')

export const absoluteFundsRequiredAtom = atom((get) => {
  const { data } = get(swapRouteAtom)
  const fromGasFeeBalance = get(chainFeeTokenBalanceAtom)

  if (!data?.route.FeeRequirements) return

  const fromToken = get(fromTokenAcrossChainsAtom).data?.requested_asset
  const chainFeeDenom = data.route.FeeRequirements.cost_check_result.fee.denom
  const amountIn = data.route.AmountIn
  const fundsBalanceDifferenceRequired =
    data.route.FeeRequirements.cost_check_result
      .funds_balance_difference_required

  if (
    // Need a valid token to check against
    !fromToken ||
    // Need a balance to calculate against
    !fromGasFeeBalance ||
    // Chain fee denom must match the token denom
    chainFeeDenom !== fromToken.denom ||
    Number(fromGasFeeBalance?.normalized_amount) <= 0 ||
    amountIn <= 0 ||
    // No funds difference required means no adjustment needed
    fundsBalanceDifferenceRequired.native === 0
  )
    return

  const absoluteFundsRequired = amountIn - fundsBalanceDifferenceRequired.native
  return absoluteFundsRequired <= 0 ? amountIn : absoluteFundsRequired
})

/*
 * This effect clears any manual insufficient fee error when the swap route is being refetched.
 * This ensures we don't show stale fee errors while new route data is being loaded.
 */
export const clearManualInsufficientBalanceEffectAtom = atomEffect(
  (get, set) => {
    const { isRefetching } = get(swapRouteAtom)

    if (isRefetching) {
      set(manualInsufficientBalanceForFeesErrorAtom, undefined)
    }
  },
)

// Destination Account Store Atoms
interface DestinationAccountState {
  open: boolean
  chainType?: ChainType | undefined
}

const _destinationAccountStateAtom = atom<DestinationAccountState>({
  open: false,
  chainType: undefined,
})

export const destinationAccountStateAtom = atom(
  (get) => ({
    open: get(_destinationAccountStateAtom).open,
    chainType: get(_destinationAccountStateAtom).chainType,
  }),
  (_get, set, update: { open: boolean; chainType?: ChainType }) => {
    set(_destinationAccountStateAtom, {
      open: update.open,
      chainType: update.chainType,
    })
  },
)

/** Helper functions for common destination account actions */
export const openDestinationAccount = (chainType?: ChainType) => ({
  open: true,
  chainType,
})

export const closeDestinationAccount = () => ({
  open: false,
  chainType: undefined,
})

// =============================================================================
// Privacy UI Feature Flag Protected Atoms
// =============================================================================
// These atoms implement the privacy vault toggle logic for logged-in users.
// ALL logic is gated behind the privacyUi feature flag to ensure zero impact
// on stage environment when flag is disabled.

/**
 * Detects if the SOURCE (from) is using the Privacy Vault account
 * FF Protected: Returns false when privacyUi flag is disabled
 */
const isSourcePrivateAtom = atom((get) => {
  const featureFlags = get(featureFlagsAtom)

  // ← FF GATE: Early return if privacyUi disabled
  if (!featureFlags.privacyUi) return false

  const fromToken = get(fromTokenAcrossChainsAtom).data

  // Simply check if source chain is Namada
  // Works for both guest and logged-in users
  return fromToken?.requested_asset?.chain_type === ChainType.Namada
})

/**
 * Detects if the DESTINATION (to) is using the Privacy Vault account
 * FF Protected: Returns false when privacyUi flag is disabled
 */
const isDestPrivateAtom = atom((get) => {
  const featureFlags = get(featureFlagsAtom)

  // ← FF GATE: Early return if privacyUi disabled
  if (!featureFlags.privacyUi) return false

  const toToken = get(toTokenAcrossChainsAtom).data

  // Simply check if destination chain is Namada
  // Works for both guest and logged-in users
  // Don't require the address to exist yet - it will be generated during swap
  return toToken?.requested_asset?.chain_type === ChainType.Namada
})

/**
 * Determines if the Go Private toggle should be visible
 * FF Protected: Returns false when privacyUi flag is disabled
 *
 * Toggle visibility rules:
 * - Show for guest users (they get login prompt when clicked)
 * - Show for logged-in users
 * - Hide if BOTH source AND destination are private
 */
export const shouldShowGoPrivateToggleAtom = atom((get) => {
  const featureFlags = get(featureFlagsAtom)

  // ← FF GATE: Early return if privacyUi disabled
  if (!featureFlags.privacyUi) return false

  const isSourcePrivate = get(isSourcePrivateAtom)
  const isDestPrivate = get(isDestPrivateAtom)

  // Hide toggle if BOTH are private
  if (isSourcePrivate && isDestPrivate) return false

  // Show toggle for all users (guests get login prompt on click)
  return true
})

/**
 * Returns the current state of the Go Private toggle (ON/OFF)
 * FF Protected: Returns false when privacyUi flag is disabled
 *
 * Toggle state rules:
 * - ON: Destination is private (source is not)
 * - OFF: No privacy OR source private but destination not
 */
export const goPrivateToggleStateAtom = atom((get) => {
  const featureFlags = get(featureFlagsAtom)

  // ← FF GATE: Early return if privacyUi disabled
  if (!featureFlags.privacyUi) return false

  const isDestPrivate = get(isDestPrivateAtom)

  // Toggle is ON when destination is private
  return isDestPrivate
})
